import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  MessageSquare, Send, Check, X, Trash2, Clock, 
  Users, Lock, Eye, EyeOff, ChevronDown, Building2
} from 'lucide-react';
import { API } from '@/App';
import { formatDate } from '@/utils/dateFormat';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const VISIBILITY_OPTIONS = [
  { value: 'all', label: 'Everyone', icon: Users, color: 'text-stone-600', description: 'Visible to all team members' },
  { value: 'client_only', label: 'Client Only', icon: Eye, color: 'text-blue-600', description: 'Hidden from vendors & labour' },
  { value: 'vendor_labour', label: 'Vendor/Labour', icon: Building2, color: 'text-amber-600', description: 'Hidden from client' },
  { value: 'internal', label: 'Internal Only', icon: Lock, color: 'text-red-600', description: 'Bella Casa team only' },
];

const getRoleColor = (role) => {
  switch (role) {
    case 'owner': return 'bg-primary text-white';
    case 'staff': return 'bg-blue-500 text-white';
    case 'client': return 'bg-green-500 text-white';
    case 'vendor': return 'bg-amber-500 text-white';
    case 'labour': return 'bg-orange-500 text-white';
    default: return 'bg-stone-500 text-white';
  }
};

const getRoleBadge = (role) => {
  const colors = {
    owner: 'bg-primary/10 text-stone-800',
    staff: 'bg-blue-100 text-blue-700',
    client: 'bg-green-100 text-green-700',
    vendor: 'bg-amber-100 text-amber-700',
    labour: 'bg-orange-100 text-orange-700',
  };
  return colors[role] || 'bg-stone-100 text-stone-700';
};

const getVisibilityBadge = (visibility) => {
  const option = VISIBILITY_OPTIONS.find(o => o.value === visibility);
  if (!option || visibility === 'all') return null;
  const Icon = option.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] ${option.color}`}>
      <Icon size={10} />
      {option.label}
    </span>
  );
};

export default function TaskCommentsSidebar({ task, isOpen, onClose, onTaskUpdated }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [visibility, setVisibility] = useState('all');
  const messagesEndRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canApprove = user.role === 'owner' || user.role === 'staff';
  const canSetVisibility = user.role === 'owner' || user.role === 'staff';

  const fetchComments = async () => {
    if (!task?.id) return;
    try {
      const response = await axios.get(`${API}/tasks/${task.id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  useEffect(() => {
    if (isOpen && task?.id) {
      fetchComments();
    }
  }, [isOpen, task?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [comments]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAddComment = async (action = null) => {
    if (!newComment.trim() && !action) {
      toast.error('Please enter a comment');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/tasks/${task.id}/comments`, {
        comment: newComment || (action === 'approve' ? 'Task approved ✓' : 'Task rejected ✗'),
        action: action,
        visibility: visibility,
      });
      
      setNewComment('');
      setVisibility('all');
      fetchComments();
      
      if (action === 'approve') {
        toast.success('Task approved');
      } else if (action === 'reject') {
        toast.success('Task rejected');
      } else {
        toast.success('Comment sent');
      }
      
      if (onTaskUpdated) onTaskUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send comment');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/tasks/${task.id}/comments/${commentId}`);
      fetchComments();
      toast.success('Comment deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete comment');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (newComment.trim()) {
        handleAddComment();
      }
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays < 7) {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return days[date.getDay()] + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return formatDate(date) + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isOwnMessage = (comment) => comment.user_id === user.id;

  const getActionBadge = (action) => {
    if (action === 'approve') {
      return (
        <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-green-100 text-green-700 text-xs font-medium">
          <Check size={12} />
          Approved
        </div>
      );
    }
    if (action === 'reject') {
      return (
        <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-red-100 text-red-700 text-xs font-medium">
          <X size={12} />
          Rejected
        </div>
      );
    }
    return null;
  };

  const selectedVisibilityOption = VISIBILITY_OPTIONS.find(o => o.value === visibility);

  if (!task) return null;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        className="w-full sm:w-[420px] p-0 flex flex-col bg-[#e5ddd5]"
        data-testid="task-comments-sidebar"
      >
        {/* Header - WhatsApp style */}
        <div className="bg-primary text-white px-4 py-3 flex items-center gap-3" data-testid="comments-header">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <MessageSquare size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold truncate">{task.title}</h3>
            <div className="flex items-center gap-2 text-xs text-white/80">
              <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                task.approved ? 'bg-green-500/30' : 'bg-amber-500/30'
              }`}>
                {task.approved ? 'Approved' : 'Pending'}
              </span>
              {/* SECURITY: Only show task_cost to non-client users */}
              {task.task_cost > 0 && user?.role !== 'client' && <span>₹{task.task_cost.toLocaleString()}</span>}
            </div>
          </div>
        </div>

        {/* Messages Area - WhatsApp style */}
        <div 
          className="flex-1 overflow-y-auto p-4 space-y-3"
          style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23d4cdc4\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}
          data-testid="comments-list"
        >
          {comments.length === 0 ? (
            <div className="text-center py-12">
              <div className="bg-white/80 rounded-xl p-6 inline-block">
                <MessageSquare size={40} className="mx-auto text-stone-400 mb-3" />
                <p className="text-stone-600 font-medium">No messages yet</p>
                <p className="text-sm text-stone-400 mt-1">Start the conversation</p>
              </div>
            </div>
          ) : (
            comments.map((comment, index) => {
              const own = isOwnMessage(comment);
              const showDateDivider = index === 0 || 
                new Date(comment.created_at).toDateString() !== new Date(comments[index - 1]?.created_at).toDateString();
              
              return (
                <div key={comment.id}>
                  {showDateDivider && (
                    <div className="flex justify-center my-4">
                      <span className="bg-white/80 text-stone-500 text-xs px-3 py-1 rounded-full shadow-sm">
                        {formatDate(new Date(comment.created_at), { includeWeekday: true })}
                      </span>
                    </div>
                  )}
                  
                  <div 
                    className={`flex ${own ? 'justify-end' : 'justify-start'} group`}
                    data-testid={`comment-${comment.id}`}
                  >
                    <div 
                      className={`relative max-w-[85%] rounded-lg shadow-sm px-3 py-2 ${
                        own 
                          ? 'bg-[#dcf8c6] rounded-tr-none' 
                          : 'bg-white rounded-tl-none'
                      }`}
                    >
                      {/* Message tail */}
                      <div 
                        className={`absolute top-0 w-3 h-3 ${
                          own 
                            ? '-right-1.5 bg-[#dcf8c6]' 
                            : '-left-1.5 bg-white'
                        }`}
                        style={{
                          clipPath: own 
                            ? 'polygon(0 0, 100% 0, 0 100%)' 
                            : 'polygon(100% 0, 0 0, 100% 100%)'
                        }}
                      />
                      
                      {/* Sender info (for others' messages) */}
                      {!own && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold ${
                            comment.user_role === 'owner' || comment.user_role === 'staff' 
                              ? 'text-stone-800' 
                              : comment.user_role === 'client' 
                                ? 'text-green-600' 
                                : 'text-amber-600'
                          }`}>
                            {comment.user_name}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] ${getRoleBadge(comment.user_role)}`}>
                            {comment.user_role}
                          </span>
                        </div>
                      )}
                      
                      {/* Action badge if present */}
                      {comment.action && (
                        <div className="mb-2">
                          {getActionBadge(comment.action)}
                        </div>
                      )}
                      
                      {/* Message content */}
                      <p className="text-sm text-stone-800 whitespace-pre-wrap break-words">
                        {comment.comment}
                      </p>
                      
                      {/* Footer with time and visibility */}
                      <div className={`flex items-center gap-2 mt-1 ${own ? 'justify-end' : 'justify-start'}`}>
                        {getVisibilityBadge(comment.visibility)}
                        <span className="text-[10px] text-stone-400">
                          {formatTime(comment.created_at)}
                        </span>
                        {own && (
                          <Check size={12} className="text-blue-500" />
                        )}
                      </div>
                      
                      {/* Delete button (visible on hover) */}
                      {(comment.user_id === user.id || user.role === 'owner') && (
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          className="absolute -top-2 -right-2 p-1.5 bg-white rounded-full shadow-md 
                            text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`delete-comment-${comment.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Approval Actions (for pending tasks) */}
        {canApprove && !task.approved && (
          <div className="bg-white border-t px-4 py-2 flex gap-2">
            <Button
              onClick={() => handleAddComment('approve')}
              disabled={loading}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              data-testid="approve-task-btn"
            >
              <Check size={16} className="mr-1" />
              Approve Task
            </Button>
            <Button
              onClick={() => handleAddComment('reject')}
              disabled={loading}
              variant="outline"
              className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
              data-testid="reject-task-btn"
            >
              <X size={16} className="mr-1" />
              Reject
            </Button>
          </div>
        )}

        {/* Message Input - WhatsApp style */}
        <div className="bg-[#f0f0f0] px-3 py-2 border-t" data-testid="comment-input-area">
          {/* Visibility selector (for owner/staff only) */}
          {canSetVisibility && (
            <div className="mb-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 text-xs text-stone-600 hover:text-stone-800 px-2 py-1 rounded bg-white/50">
                    {selectedVisibilityOption && (
                      <>
                        <selectedVisibilityOption.icon size={14} className={selectedVisibilityOption.color} />
                        <span>{selectedVisibilityOption.label}</span>
                      </>
                    )}
                    <ChevronDown size={12} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {VISIBILITY_OPTIONS.map((option) => (
                    <DropdownMenuItem
                      key={option.value}
                      onClick={() => setVisibility(option.value)}
                      className="flex items-start gap-3 py-2"
                    >
                      <option.icon size={16} className={option.color} />
                      <div>
                        <div className="font-medium text-sm">{option.label}</div>
                        <div className="text-xs text-stone-400">{option.description}</div>
                      </div>
                      {visibility === option.value && (
                        <Check size={14} className="ml-auto text-stone-800" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          
          <div className="flex items-end gap-2">
            <Textarea
              placeholder="Type a message..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={handleKeyPress}
              rows={1}
              className="resize-none bg-white rounded-xl border-0 shadow-sm focus-visible:ring-1 
                focus-visible:ring-primary min-h-[42px] max-h-[120px]"
              style={{ height: 'auto' }}
              data-testid="comment-input"
            />
            <Button
              onClick={() => handleAddComment()}
              disabled={loading || !newComment.trim()}
              className="rounded-full w-10 h-10 p-0 bg-primary hover:bg-primary/90"
              data-testid="send-comment-btn"
            >
              <Send size={18} />
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
